import { RecordLockedError, SchemaLockedError } from "@auger/core"

/**
 * Core starts its error messages with the name of the function that threw
 * ("buildObjects: …", "rows.update: …"), and a wrapped error repeats that
 * inside the message. Those names help a log reader or an MCP agent, not a
 * user reading a toast ("Failed to create time: buildObjects: …").
 *
 * Leading names are any lowercase identifier ("update: "). Inner ones, after
 * ": ", must be camelCase or dotted ("applyRelationOps: ", "rows.update: "), so
 * plain words in a message stay.
 */
const LEADING_NAMES = /^(?:[a-z]\w*(?:\.[a-z]\w*)*: )+/
const INNER_NAMES = /: (?:[a-z]\w*(?:[A-Z]\w*|\.[a-z]\w*)+: )+/g

/**
 * An error's message as a toast should show it: core's function-name prefixes
 * stripped. The error itself is not changed, so log the error object when the
 * full text matters. Every toast that shows an error message goes through
 * this (eslint.config.js enforces it). `subject` names the schema a
 * locked-schema refusal is about; a locked-record refusal always names the
 * record the database refused, whatever the subject.
 */
export function errorMessage(err: unknown, subject?: { name?: string | null }): string {
  if (err == null) return ""
  // A write refused because a record is locked: say what to do about it,
  // naming the locked record by its id, and that the write's other changes
  // were saved when core says some were.
  if (err instanceof RecordLockedError) {
    const who = err.recordId != null ? `#${err.recordId}` : "This record"
    const saved = err.partialWrite ? " Your other changes were saved." : ""
    return `${who} is locked. Unlock it to change it.${saved}`
  }
  // A schema a locked list uses: the schema is named by the caller or by id.
  if (err instanceof SchemaLockedError) {
    const who = subject?.name || `Schema ${err.schemaId}`
    return `${who} is used by locked list ${err.lists[0]!.name}. Unlock the list to change it.`
  }
  const message =
    typeof err === "string"
      ? err
      : typeof (err as { message?: unknown }).message === "string"
        ? (err as { message: string }).message
        : String(err)
  return message.replace(LEADING_NAMES, "").replace(INNER_NAMES, ": ")
}
